import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageSquareText, ScrollText, Trash2 } from "lucide-react";
import { fmtDate } from "../proposal/format";
import { useCanAct } from "./StaffAmendPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const FORM_STAGES = ["commissioned", "medicys_sow", "sign_failed"];

function OpenRequest({ req, cid, canAct, onChanged }) {
  const [busy, setBusy] = useState(false);
  const close = async () => {
    if (!window.confirm("Close this request without changing the document? You reply to the client yourself.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/sow/${cid}/change-requests/${req.id}/close`, {});
      toast.success("Request closed. The client's page now says the document is unchanged.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close the request.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm" data-testid={`staff-change-request-${req.id}`}>
      <p className="flex items-center gap-2 font-semibold text-amber-100">
        <MessageSquareText className="w-4 h-4" /> {req.requested_by_name || req.requested_by} asked for a change on {fmtDate(req.requested_at)}
        {req.requested_by_name ? <span className="font-normal text-amber-200/80">({req.requested_by})</span> : null}
      </p>
      <p className="whitespace-pre-line text-stone-200" data-testid={`staff-change-request-${req.id}-text`}>{req.text}</p>
      {canAct && (
        <button type="button" onClick={close} disabled={busy} className="self-start inline-flex items-center gap-1.5 text-xs text-stone-300 underline underline-offset-2 hover:text-white disabled:opacity-50" data-testid={`staff-change-request-${req.id}-close`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Close without changing the document
        </button>
      )}
    </div>
  );
}

// Medicys only panel in the viewer: the client's change requests and the
// special conditions agreed for this project (section 6 of the SOW).
export function StaffSpecialConditionsPanel({ token, onChanged = null }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const url = `${API}/api/public/proposals/${encodeURIComponent(token)}/commission/countersign-review`;
  const canAct = useCanAct(data?.bd_email);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(url, { withCredentials: true });
      setData(d);
    } catch (_e) {
      setData(null);
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const sc = data.special_conditions;
  const requests = data.change_requests || [];
  const open = requests.filter((r) => r.status === "open");
  if (!sc && !open.length && !FORM_STAGES.includes(data.stage)) return null;
  const editable = !!data.special_conditions_editable;
  const changed = () => { load(); onChanged?.(); };

  const save = async (value) => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/sow/${data.cid}/special-conditions`, { text: value, notify_client: !!value && notify });
      toast.success(value ? (notify ? "Special conditions recorded and the client has been emailed to review and sign." : "Special conditions recorded. They appear in the draft the client reviews.") : "Special conditions removed.");
      setEditing(false);
      changed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the special conditions.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => { setText(sc?.text || ""); setNotify(true); setEditing(true); };

  return (
    <section className="no-print mx-4 sm:mx-6 lg:mx-10 mt-4 rounded-xl border border-[#1B4F8A]/30 bg-[#0B1220] text-stone-100 p-5 sm:p-6 flex flex-col gap-4" data-testid="staff-special-conditions-panel" data-has-conditions={sc ? "yes" : "no"} data-open-requests={open.length}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#3EA8DC]/15 text-[#9FD8F2] flex items-center justify-center shrink-0"><ScrollText className="w-4 h-4" /></span>
        <div className="flex flex-col gap-1.5 text-[15px] leading-relaxed">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#9FD8F2]">Medicys only · Special conditions</p>
          <p>
            {open.length
              ? <span className="font-semibold">{data.client_company || "The client"} has asked for a change before signing.</span>
              : sc
                ? <span className="font-semibold">Special conditions are recorded on this Statement of Work.</span>
                : <span className="font-semibold">No special conditions on this Statement of Work.</span>}
            {" "}Agreed variations to the Terms are recorded here, appear as section 6 of the draft and the signed PDF, and prevail over Schedule 1 where they conflict.
          </p>
        </div>
      </div>

      {open.map((r) => <OpenRequest key={r.id} req={r} cid={data.cid} canAct={canAct} onChanged={changed} />)}

      {sc && !editing && (
        <div className="flex flex-col gap-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm" data-testid="staff-special-conditions-current">
          <p className="whitespace-pre-line text-stone-100" data-testid="staff-special-conditions-text">{sc.text}</p>
          <p className="text-xs text-stone-400">Recorded by {sc.set_by_name} on {fmtDate(sc.set_at)}.</p>
        </div>
      )}

      {editing ? (
        <div className="flex flex-col gap-3 rounded-lg bg-white/5 border border-white/10 p-4" data-testid="staff-special-conditions-form">
          <label className="flex flex-col gap-1.5 text-sm text-stone-200">
            <span className="font-semibold">The agreed wording, exactly as it should appear in section 6</span>
            <span className="text-xs text-stone-400">Name the clause being varied and state the replacement. Separate paragraphs with a blank line. Agree the text with the client by email before recording it here.</span>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={"e.g. Clause 18 (Indemnity) of the Medicys Terms and Conditions of Business is replaced as follows. Each party shall indemnify the other against ..."}
              className="mt-1 rounded-md border border-white/15 bg-[#0B1220] px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-[#3EA8DC]" data-testid="staff-special-conditions-textarea" />
          </label>
          <label className="flex items-center gap-2.5 text-sm text-stone-200 cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 accent-[#3EA8DC]" data-testid="staff-special-conditions-notify" />
            Email the client that the conditions are in place and ask them to review and sign
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => save(text.trim())} disabled={busy || text.trim().length < 10} className="inline-flex items-center gap-2 rounded-full bg-[#3EA8DC] px-5 py-2.5 text-sm font-semibold text-[#0B1220] hover:bg-[#5FBCE6] disabled:opacity-40 transition-colors" data-testid="staff-special-conditions-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {sc ? "Save the changes" : "Record the special conditions"}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="text-sm text-stone-400 hover:text-stone-200" data-testid="staff-special-conditions-cancel">Cancel</button>
            {sc && (
              <button type="button" onClick={() => window.confirm("Remove the special conditions from this Statement of Work?") && save("")} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 text-xs text-rose-300 hover:text-rose-200" data-testid="staff-special-conditions-remove">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
        </div>
      ) : canAct && editable ? (
        <button type="button" onClick={startEdit} className="self-start inline-flex items-center gap-2 rounded-full border border-[#3EA8DC]/60 px-4 py-2 text-sm font-semibold text-[#9FD8F2] hover:bg-[#3EA8DC]/10 transition-colors" data-testid="staff-special-conditions-edit">
          <ScrollText className="w-4 h-4" /> {sc ? "Edit the special conditions" : "Record special conditions"}
        </button>
      ) : !editable ? (
        <p className="text-xs text-stone-400" data-testid="staff-special-conditions-locked">The client has signed, so the conditions cannot change here. Return the document for changes before countersigning, or amend and re-issue it once executed.</p>
      ) : (
        <p className="text-xs text-stone-400" data-testid="staff-special-conditions-not-allowed">Only the BD owner of this proposal{data.bd_poc ? ` (${data.bd_poc})` : ""} or an admin can record special conditions.</p>
      )}
    </section>
  );
}
